import { EmissionValueUnit } from '@api-client/api'
import { TFunction } from 'i18next'
import { ceil, round as _round, toNumber } from 'lodash-es'

import { isValidArray } from './arrays'

export interface IAutoNumberFormatterOptions {
	locale?: string
	suffix?: boolean
	decimals?: number
	removeZeroDecimals?: boolean
	suffixes?: readonly { factor: number; isDefault?: boolean; suffix: string }[]
	extraSuffix?: string
}

const defaultUnits = [
	{ factor: 1000000, suffix: ' M' },
	{ factor: 1000, suffix: ' k' },
	{ factor: 1, suffix: '' },
] as const

export const weightUnits = [
	{ factor: 1_000_000_000_000, suffix: ' Gt' },
	{ factor: 1_000_000_000, suffix: ' Mt' },
	{ factor: 1_000_000, suffix: ' kt' },
	{ factor: 1_000, suffix: ' t' },
	{ factor: 1, isDefault: true, suffix: ' kg' },
	{ factor: 0.001, suffix: ' g' },
	{ factor: 0.000001, suffix: ' mg' },
]

const autoNumberFormatter = (
	num: string | number,
	{
		locale = 'en-US',
		decimals: _decimals,
		removeZeroDecimals = false,
		suffixes = defaultUnits,
		extraSuffix = 'kg',
	}: IAutoNumberFormatterOptions = {}
) => {
	if (!isNumber(num) && !isNumberStr(num)) {
		return num
	}
	const _num = Number(num)
	const absNum = Math.abs(_num)

	for (const [index, { factor, isDefault, suffix }] of suffixes.entries()) {
		if (
			absNum < factor &&
			index < suffixes.length - 1 &&
			(absNum > 0 || !isDefault)
		) {
			continue
		}
		const decimals = isNumber(_decimals)
			? _decimals
			: getDecimalPlaces(_num / (suffix ? factor : 1))

		const formattedNumber = toEnStr(_num / factor, { decimals, locale })

		return (
			(removeZeroDecimals
				? removeAllZeroDecimals(formattedNumber)
				: formattedNumber) +
			(suffix + extraSuffix)
		)
	}
	return num + ''
}

const formatEmissions = (
	value: number,
	perUnit: EmissionValueUnit | undefined,
	t: TFunction<'translation', string>
) => {
	if (perUnit === EmissionValueUnit.OTHER) {
		return toEnStr(value)
	}
	return autoNumberFormatter(value, {
		suffixes: weightUnits,
		extraSuffix: t('emissions.unit.name', {
			context: perUnit || EmissionValueUnit.KGCO2_EQ_KG,
		}).replace(/^kg/, ''),
	})
}

const getDecimalPlaces = (num: number, max = 2) => {
	//get the number of decimal places in the number passed, capped to a maximum of 2
	return Math.min(
		max,
		Math.floor(num) === num ? 0 : (getDecimalPartFromNumber(num) || '').length
	)
}

const getNumberLength = (val: number | string) => {
	if (isNumber(val)) {
		return String(val).replace('.', '').length
	} else if (typeof val === 'string') {
		return val.replace('.', '').length
	} else {
		return 0
	}
}

// if val = 1.00 -> 1 else if val = 1.01 -> 1.01
const removeAllZeroDecimals = (val: string) => {
	const [int, decs] = val.split('.')

	if (!decs) {
		return val
	} else {
		const suffix = decs.match(/[a-z\s]/gi)?.join('') ?? ''
		const isAllZeroes = decs.replace(/[0\D]+/gi, '').length === 0

		return isAllZeroes ? `${int}${suffix}` : val
	}
}

const getDecimalPartFromNumber = (num: string | number) => {
	if (isNumeric(num)) {
		return String(num).split('.')[1]
	} else {
		return undefined
	}
}

const round = (num: number, factor: number) => {
	return Math.round((num + Number.EPSILON) * factor) / factor
}

interface IToEnStrProps {
	locale?: string
	decimals?: number
}

const toEnStr = (
	num: number,
	{ decimals = 0, locale = 'en-US' }: IToEnStrProps = {}
) => {
	return num.toLocaleString(locale, {
		minimumFractionDigits: decimals,
		maximumFractionDigits: decimals,
	})
}

const numberWithCommas = (x: number | string) => {
	if (isNumeric(x)) {
		const _x = toNumber(x)
		return _x
			.toFixed(0)
			.toString()
			.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
	} else {
		return `${x}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
	}
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isNumeric = (val: any) => {
	return /^-?[\d.]+(?:e-?\d+)?$/.test(val)
}

const isValue = (val: unknown) => val !== null && val !== undefined

const isNumberStr = (val: unknown) => {
	return isNumber(Number(val)) && isValue(val)
}

const isNaN = (val: unknown) => Number.isNaN(val)

const isNumber = (val: unknown): val is number => {
	return typeof val === 'number' && !isNaN(val)
}

const isNumberOrNumberStr = (val: unknown) => {
	return isNumber(val) || isNumberStr(val)
}

const percentageAutoRounding = (x: number | string | null | undefined) => {
	if (isNumeric(x)) {
		let _finalVal = toNumber(x) * 100
		const _decimalPart = getDecimalPartFromNumber(_finalVal)
		if (_decimalPart) {
			if (_finalVal >= 10) {
				_finalVal = toNumber(numberRounding(_finalVal, 0))
			} else {
				_finalVal = toNumber(numberRounding(_finalVal, 1))
			}
		}
		return _finalVal / 100
	} else {
		return x
	}
}

const numberRounding = (x: number | string, r: number) => {
	if (isNumeric(x)) {
		const _x = toNumber(x)
		return _round(_x, r || 0)
	} else {
		return x
	}
}

const numberRoundingUP = (x: number | string, r: number) => {
	if (isNumeric(x)) {
		const _x = toNumber(x)
		return ceil(_x, r || 0)
	} else {
		return x
	}
}

const getQuantileSorted = (arr: number[], quantile: number) => {
	const pos = (arr.length - 1) * quantile
	const base = Math.floor(pos)
	const rest = pos - base
	if (arr[base + 1] !== undefined) {
		return arr[base] + rest * (arr[base + 1] - arr[base])
	} else {
		return arr[base]
	}
}

//array must be sorted
const getQuantile = (_arr: number[], quantile: number) => {
	const arr = _arr.sort((a, b) => a - b)
	return getQuantileSorted(arr, quantile)
}

const getSortedMinAndMax = (arr: number[]) =>
	isValidArray(arr) ? [arr[0], arr[arr.length - 1]] : []

const getMinAndMax = (arr: number[]) =>
	isValidArray(arr) ? [Math.min(...arr), Math.max(...arr)] : []

const multiplyDecimalNumberWith100 = (num: string | number) => {
	// function will be useful to solve the decimal error (js can not handle decimal value multiply with 100)
	// normal multiplication add addition decimal like 00000000000001 e.x. 0.56*100 = 56.00000000000001
	// this issue never occur always but happens some time
	if (isNumeric(num)) {
		const _val = toNumber(num)
		return +(_val * 100).toFixed(1)
	} else {
		return +num
	}
}

const calcPercentage = (total: number, value: number) =>
	total === 0 ? 0 : (value * 100) / total

export {
	autoNumberFormatter,
	calcPercentage,
	formatEmissions,
	getDecimalPartFromNumber,
	getMinAndMax,
	getNumberLength,
	getQuantile,
	getQuantileSorted,
	getSortedMinAndMax,
	isNaN,
	isNumber,
	isNumberOrNumberStr,
	isNumberStr,
	isNumeric,
	multiplyDecimalNumberWith100,
	numberRounding,
	numberRoundingUP,
	numberWithCommas,
	percentageAutoRounding,
	round,
}
